import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { euckrBytes, prettyPhone } from "../api";
import "../App.css";

const PHONE_HEADERS = ["번호", "전화", "휴대폰", "핸드폰", "연락처", "폰", "phone", "mobile", "tel"];

function pickPhoneKey(row) {
  const keys = Object.keys(row);
  const byName = keys.find((k) =>
    PHONE_HEADERS.some((h) => String(k).toLowerCase().includes(h.toLowerCase()))
  );
  if (byName) return byName;
  // 헤더 이름으로 못 찾으면, 값이 전화번호처럼 생긴 칸을 쓴다
  return keys.find((k) => /^0\d[\d\-\s]{7,}$/.test(String(row[k] || "").trim()));
}

const Send = () => {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [numbers, setNumbers] = useState("");
  const [sender] = useState("0612778002");
  const [templates, setTemplates] = useState([]);
  const [rows, setRows] = useState(null);          // 엑셀로 넣은 경우 건별 치환값
  const [reserve, setReserve] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [check, setCheck] = useState(null);        // dryRun 결과
  const [busy, setBusy] = useState(false);
  const [group, setGroup] = useState(null);        // {groupId, summary, rows}
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);
  const timer = useRef(null);

  const bytes = euckrBytes(content);
  const kind = bytes <= 90 ? "SMS" : "LMS";

  useEffect(() => {
    api.post("/searchTemplate").then((r) => setTemplates(r.data || [])).catch(() => {});
    return () => clearInterval(timer.current);
  }, []);

  const recipients = () => {
    if (rows && rows.length) return rows;
    return numbers
      .split(/[\s,;]+/)
      .filter(Boolean)
      .map((p) => ({ phone: p, vars: null }));
  };

  const count = recipients().length;

  const readExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "buffer" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!data.length) { setNotice("엑셀 첫 시트에서 읽을 줄이 없습니다."); return; }
        const key = pickPhoneKey(data[0]);
        if (!key) { setNotice("전화번호 칸을 찾지 못했습니다. 머리글에 '번호'나 '연락처'를 넣어 주세요."); return; }
        const parsed = data
          .map((r) => ({ phone: String(r[key] || "").trim(), vars: { ...r } }))
          .filter((r) => r.phone);
        setRows(parsed);
        setNumbers(parsed.map((r) => r.phone).join("\n"));
        setNotice(
          `엑셀 ${parsed.length}건 읽음 · 번호칸="${key}" · 치환 가능: ` +
          Object.keys(data[0]).map((k) => `#{${k}}`).join(" ")
        );
      } catch (err) {
        setNotice("엑셀을 읽지 못했습니다: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const doCheck = async () => {
    setNotice("");
    try {
      const res = await api.post("/ui/send", {
        content, title, sender, recipients: recipients(), dryRun: true,
      });
      setCheck(res.data);
    } catch (err) {
      const d = err.response?.data;
      setCheck(d && d.rejected ? { ...d, dryRun: true } : null);
      setNotice(d?.error || "확인 중 오류가 발생했습니다.");
    }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const body = { content, title, sender, recipients: recipients() };
      if (reserve && sendAt) body.sendAt = sendAt;
      const res = await api.post("/ui/send", body);

      if (res.data.scheduled) {
        setCheck(null);
        setNotice(`${res.data.sendAt} 에 ${res.data.count}건 예약했습니다. (예약함에서 취소할 수 있습니다)`);
        setBusy(false);
        return;
      }
      setCheck(null);
      setGroup({ groupId: res.data.groupId, rows: [], summary: null, rejected: res.data.rejected });
      track(res.data.groupId);
    } catch (err) {
      setNotice(err.response?.data?.error || "발송에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  // 보낸 뒤 실제로 도착했는지를 3초마다 다시 물어본다
  const track = (groupId) => {
    clearInterval(timer.current);
    const poll = async () => {
      try {
        const r = await api.get(`/ui/group/${groupId}`);
        setGroup((g) => ({ ...(g || {}), groupId, rows: r.data.rows, summary: r.data.summary }));
        if (r.data.summary && r.data.summary.pending === 0) clearInterval(timer.current);
      } catch (err) { /* 다음 주기에 다시 본다 */ }
    };
    poll();
    timer.current = setInterval(poll, 3000);
  };

  const resendFailed = () => {
    const failed = (group.rows || []).filter((r) => r.ok === false).map((r) => r.phone);
    if (!failed.length) return;
    setNumbers(failed.join("\n"));
    setRows(null);
    setGroup(null);
    setNotice(`실패한 ${failed.length}건을 수신번호에 다시 넣었습니다. 내용을 확인하고 발송하세요.`);
  };

  return (
    <div className="send">
      <h2 className="title">메시지 발송</h2>

      <div className="send-grid">
        <section className="card">
          <div className="card-head">
            <span>보낼 내용</span>
            <span className={"chip " + (kind === "LMS" ? "warn" : "")}>
              {kind} · {bytes}바이트 {kind === "SMS" ? "(90까지 SMS)" : "(90 초과 → 장문)"}
            </span>
          </div>

          <div className="row">
            <select
              className="input"
              value=""
              onChange={(e) => {
                const t = templates.find((x) => String(x.id) === e.target.value);
                if (t) { setContent(t.content); if (t.title) setTitle(t.title); }
              }}
            >
              <option value="">템플릿 불러오기…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.title || `템플릿 ${t.id}`}</option>
              ))}
            </select>
          </div>

          {kind === "LMS" && (
            <input
              className="input"
              placeholder="장문 제목 (선택)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={60}
            />
          )}

          <textarea
            className="msg"
            placeholder="보낼 내용을 입력하세요.  엑셀로 넣으면 #{이름} 처럼 칸 이름을 넣어 사람마다 다르게 보낼 수 있습니다."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          <div className="hint">
            발신번호 <b>{prettyPhone(sender)}</b> · 이모지·이미지는 보낼 수 없습니다
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <span>받는 사람</span>
            <span className="chip">{count}명</span>
          </div>

          <div className="row">
            <button className="btn ghost sm" onClick={() => fileRef.current.click()}>엑셀 불러오기</button>
            <button className="btn ghost sm" onClick={() => { setNumbers(""); setRows(null); setNotice(""); }}>비우기</button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => { if (e.target.files[0]) readExcel(e.target.files[0]); e.target.value = ""; }}
            />
          </div>

          <textarea
            className="numbers"
            placeholder={"01012345678\n01023456789\n\n한 줄에 하나씩 (쉼표·공백도 됩니다)"}
            value={numbers}
            onChange={(e) => { setNumbers(e.target.value); setRows(null); }}
          />

          <label className="row check">
            <input type="checkbox" checked={reserve} onChange={(e) => setReserve(e.target.checked)} />
            예약 발송
            {reserve && (
              <input
                className="input"
                type="datetime-local"
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            )}
          </label>

          <div className="row end">
            <button className="btn primary" disabled={!content.trim() || !count || busy} onClick={doCheck}>
              발송 확인
            </button>
          </div>
        </section>
      </div>

      {notice && <div className="notice">{notice}</div>}

      {check && (
        <div className="modal-back" onClick={() => setCheck(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>이대로 보낼까요?</h3>
            <div className="confirm-grid">
              <div><span>보낼 건수</span><b>{(check.accepted || []).length}건</b></div>
              <div><span>종류</span><b>{check.kind || kind} · {check.bytes || bytes}바이트</b></div>
              <div><span>예상 소요</span><b>약 {check.etaSec || 1}초</b></div>
              <div><span>발송 시각</span><b>{reserve && sendAt ? sendAt.replace("T", " ") : "지금 바로"}</b></div>
            </div>

            {!!(check.rejected || []).length && (
              <div className="reject">
                <b>보내지 않는 번호 {check.rejected.length}건</b>
                <ul>
                  {check.rejected.slice(0, 10).map((r, i) => (
                    <li key={i}>{r.phone} — {r.reason}</li>
                  ))}
                  {check.rejected.length > 10 && <li>… 외 {check.rejected.length - 10}건</li>}
                </ul>
              </div>
            )}

            <pre className="preview">{content}</pre>

            <div className="row end">
              <button className="btn ghost" onClick={() => setCheck(null)}>취소</button>
              <button className="btn primary" disabled={busy || !(check.accepted || []).length} onClick={doSend}>
                {busy ? "보내는 중…" : "발송"}
              </button>
            </div>
          </div>
        </div>
      )}

      {group && (
        <section className="card result">
          <div className="card-head">
            <span>발송 결과 ({group.groupId})</span>
            {group.summary && group.summary.pending > 0 && <span className="chip">확인 중…</span>}
          </div>

          {group.summary && (
            <div className="summary">
              <div className="s all"><b>{group.summary.total}</b><span>보냄</span></div>
              <div className="s ok"><b>{group.summary.success}</b><span>도착</span></div>
              <div className="s bad"><b>{group.summary.fail}</b><span>실패</span></div>
              <div className="s wait"><b>{group.summary.pending}</b><span>대기</span></div>
            </div>
          )}

          <table className="tbl">
            <thead>
              <tr><th>수신번호</th><th>상태</th><th>사유</th></tr>
            </thead>
            <tbody>
              {(group.rows || []).map((r) => (
                <tr key={r.uid}>
                  <td>{prettyPhone(r.phone)}</td>
                  <td><span className={"state " + (r.ok === true ? "ok" : r.ok === false ? "bad" : "wait")}>{r.state}</span></td>
                  <td className="reason">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="row end">
            {group.summary && group.summary.fail > 0 && (
              <button className="btn ghost" onClick={resendFailed}>실패한 번호만 다시 넣기</button>
            )}
            <button className="btn ghost" onClick={() => { clearInterval(timer.current); setGroup(null); }}>닫기</button>
          </div>
        </section>
      )}
    </div>
  );
};

export default Send;
